import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { useSignUpStore } from '../../../application/store/SignUpStore';
import { useAuth } from '@shared/hooks/useAuth';
import { useAuthStore } from '../../../application/store/AuthStore';
import { container } from '@infrastructure/container';
import { getPendingShopifyInstall } from '../../../features/connections/shopify/pendingInstall';


interface PasswordRequirement {
  label: string;
  regex: RegExp;
  met: boolean;
}

const SignUpPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    { label: 'Minimum length: 8 characters', regex: /.{8,}/, met: false },
    { label: 'Include at least one uppercase letter (A-Z)', regex: /[A-Z]/, met: false },
    { label: 'Include at least one lowercase letter (a-z)', regex: /[a-z]/, met: false },
    { label: 'Include at least one digit (0-9)', regex: /[0-9]/, met: false },
    { label: 'Include at least one special character (!, @, #, $, %, ^, &, *)', regex: /[!@#$%^&*]/, met: false },
  ]);

  useEffect(() => {
    setRequirements(prev =>
      prev.map(req => ({
        ...req,
        met: req.regex.test(password)
      }))
    );
  }, [password]);

  const allRequirementsMet = requirements.every(req => req.met);

  const registerUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== '' && !allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    const onboardingUser = useSignUpStore.getState();

    if (!onboardingUser.email || !onboardingUser.name || !onboardingUser.companyName) {
      setError('Something went wrong');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await container.authRepository.registerUser(
        onboardingUser.email,
        onboardingUser.name,
        onboardingUser.companyName,
        onboardingUser.termsAccepted, 
        password === '' ? undefined : password,
        onboardingUser.discountCode ?? undefined,
        getPendingShopifyInstall() ? 'shopify' : undefined,
        onboardingUser.referralCode ?? undefined,
      );

      if (response.status === 'error') {
        setError(response.error.message || 'Failed to register user');
        return;
      }

      await loginUser();
    } catch (error) {
      setError('Failed to register user');
    } finally {
      setIsLoading(false);
    }
  };

  const loginUser = async () => {
    useSignUpStore.getState().reset();

    const user = await container.authRepository.getCurrentUser();
    const token = await useAuthStore.getState().getJwt();
    const companies = await container.companyRepository.getCompanies();

    if (companies.status === 'error') {
      setError('No companies found');
      return;
    }

    const company = companies.data[0];

    if (token && user.status === 'success' && user.data) {
      login(user.data, token, company);
      // Shopify installers already connected their store — send them to claim the
      // Shopify connection and approve billing instead of the generic marketplaces grid.
      navigate(getPendingShopifyInstall() ? '/sign-up/shopify' : '/sign-up/marketplaces');
    } else {
      setError('Invalid token');
    }
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="medium" gutterBottom>
        Create a password
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Make sure your password is secure
      </Typography>
      <form onSubmit={registerUser}>
        <TextField
          fullWidth
          type="password"
          label="Password"
          variant="outlined"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
          }}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          type="password"
          label="Confirm Password"
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setError(null);
          }}
          error={!!error && error === 'Passwords do not match'}
          helperText={error === 'Passwords do not match' ? error : ''}
          sx={{ mb: 3 }}
        />

        <Box sx={{ mb: 3 }}>
          {requirements.map((req, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                color: req.met ? 'success.main' : 'text.secondary',
                mb: 0.5,
                typography: 'caption'
              }}
            >
              {/* {req.met ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <CloseIcon sx={{ fontSize: 16 }} />
              )} */}
              {req.label}
            </Stack>
          ))}
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 3, justifyContent: 'space-between' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(-1)}
            size="medium"
          >
            Back
          </Button>

          {password ? (
            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="medium"
              disabled={!allRequirementsMet || !confirmPassword || isLoading}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Creating Account...
                </Box>
              ) : (
                'Next'
              )}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              disabled={isLoading}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Creating Account...
                </Box>
              ) : (
                'Skip'
              )}
            </Button>
          )}
        </Stack>


      </form>
    </Box>
  );
};

export default SignUpPassword; 